import React, { useEffect, useState } from 'react';
import api, { notificationAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { Bell, Check, Clock, BookOpen, FileText, MessageCircle, Info } from 'lucide-react';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [notifications, setNotifications] = useState([]);
  const [infoPosts, setInfoPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // info, all, unread, read

  useEffect(() => {
    if (activeTab === 'info') {
      if (selectedCategory) {
        fetchInfoPosts(selectedCategory);
      }
    } else {
      fetchNotifications();
    }
  }, [activeTab, selectedCategory]);

  const fetchInfoPosts = async (category) => {
    try {
      setLoading(true);
      const response = await api.get('/ctv/info-posts/published', {
        params: category ? { category } : {},
      });
      setInfoPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch info posts:', error);
      setInfoPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedPost(null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedPost(null);
    setInfoPosts([]);
  };

  const fetchPostDetail = async (postId) => {
    try {
      const response = await api.get(`/ctv/info-posts/published/${postId}`);
      setSelectedPost(response.data.post);
    } catch (error) {
      console.error('Failed to fetch post detail:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const params = activeTab === 'unread' ? { unreadOnly: true } : {};
      const response = await notificationAPI.getAll(params);
      // API returns { notifications: [], pagination: {}, unreadCount: 0 }
      let notifs = response.data?.notifications || [];

      // Filter based on active tab
      if (activeTab === 'read') {
        notifs = notifs.filter(n => n.isRead);
      }

      setNotifications(notifs);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // Mock data for development
      const mockData = [
        {
          id: 1,
          title: 'Hồ sơ được duyệt',
          message: 'Khoản vay KV-2024-0123 của khách hàng Nguyễn Văn A đã được duyệt',
          type: 'success',
          isRead: false,
          createdAt: '2024-11-09T10:30:00Z',
        },
        {
          id: 2,
          title: 'Đã giải ngân',
          message: 'Khoản vay KV-2024-0120 đã được giải ngân. Hoa hồng: 1,000,000 KRW',
          type: 'success',
          isRead: false,
          createdAt: '2024-11-08T14:20:00Z',
        },
        {
          id: 3,
          title: 'Hồ sơ bị từ chối',
          message: 'Khoản vay KV-2024-0119 bị từ chối. Lý do: Thiếu giấy tờ',
          type: 'error',
          isRead: true,
          createdAt: '2024-11-07T09:15:00Z',
        },
      ];

      // Filter mock data
      if (activeTab === 'unread') {
        setNotifications(mockData.filter(n => !n.isRead));
      } else if (activeTab === 'read') {
        setNotifications(mockData.filter(n => n.isRead));
      } else {
        setNotifications(mockData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: true } : notif))
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return t('justNow');
    if (seconds < 3600) return `${Math.floor(seconds / 60)} ${t('minutesAgo')}`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} ${t('hoursAgo')}`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} ${t('daysAgo')}`;
    return date.toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <div className="notif-icon success">✓</div>;
      case 'error':
        return <div className="notif-icon error">✕</div>;
      case 'warning':
        return <div className="notif-icon warning">!</div>;
      default:
        return <div className="notif-icon info">i</div>;
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const infoSections = [
    {
      id: 'guideline',
      icon: BookOpen,
      title: language === 'vi' ? 'Hướng dẫn sử dụng' : language === 'ko' ? '사용 가이드' : 'User Guide',
      description: language === 'vi' ? 'Hướng dẫn chi tiết cách sử dụng hệ thống CTV' : language === 'ko' ? 'CTV 시스템 사용 방법에 대한 자세한 안내' : 'Detailed guide on how to use CTV system',
    },
    {
      id: 'document',
      icon: FileText,
      title: language === 'vi' ? 'Tài liệu tham khảo' : language === 'ko' ? '참고 문서' : 'Reference Documents',
      description: language === 'vi' ? 'Các tài liệu, biểu mẫu và quy định cần thiết' : language === 'ko' ? '필요한 문서, 양식 및 규정' : 'Necessary documents, forms and regulations',
    },
    {
      id: 'faq',
      icon: MessageCircle,
      title: language === 'vi' ? 'Câu hỏi thường gặp' : language === 'ko' ? '자주 묻는 질문' : 'FAQ',
      description: language === 'vi' ? 'Giải đáp các thắc mắc phổ biến' : language === 'ko' ? '일반적인 질문에 대한 답변' : 'Answers to common questions',
    },
    {
      id: 'policy',
      icon: Info,
      title: language === 'vi' ? 'Chính sách & Quy định' : language === 'ko' ? '정책 및 규정' : 'Policies & Regulations',
      description: language === 'vi' ? 'Chính sách hoa hồng, quy định làm việc' : language === 'ko' ? '수수료 정책, 작업 규정' : 'Commission policies, work regulations',
    },
  ];

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="page-header">
        <h1 className="page-title">
          {t('notifications')}
          {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
        </h1>
        {unreadCount > 0 && activeTab !== 'info' && (
          <button className="mark-all-btn" onClick={handleMarkAllAsRead}>
            <Check size={16} />
            {t('markAllRead')}
          </button>
        )}
      </div>

      {/* 4 Tabs */}
      <div className="notification-tabs">
        <button
          className={`notification-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <BookOpen size={18} />
          <span>{language === 'vi' ? 'Thông tin' : language === 'ko' ? '정보' : 'Info'}</span>
        </button>
        <button
          className={`notification-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <Bell size={18} />
          <span>{t('all')}</span>
        </button>
        <button
          className={`notification-tab ${activeTab === 'unread' ? 'active' : ''}`}
          onClick={() => setActiveTab('unread')}
        >
          <span>{t('unread')}</span>
          {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
        <button
          className={`notification-tab ${activeTab === 'read' ? 'active' : ''}`}
          onClick={() => setActiveTab('read')}
        >
          <Check size={18} />
          <span>{t('read')}</span>
        </button>
      </div>

      <div className="page-content">
        {/* Info Tab Content */}
        {activeTab === 'info' && (
          <div className="info-section-wrapper">
            {selectedPost ? (
              // Post Detail View
              <div className="post-detail">
                <button className="back-btn" onClick={() => setSelectedPost(null)}>
                  ← {t('back')}
                </button>
                <div className="post-header">
                  <h2 className="post-title">
                    {language === 'ko' && selectedPost.titleKo ? selectedPost.titleKo :
                     language === 'en' && selectedPost.titleEn ? selectedPost.titleEn :
                     selectedPost.title}
                  </h2>
                  <div className="post-meta">
                    <span>{new Date(selectedPost.publishedAt || selectedPost.createdAt).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')}</span>
                    <span>👁 {selectedPost.viewCount || 0}</span>
                  </div>
                </div>
                <div className="post-content" dangerouslySetInnerHTML={{
                  __html: language === 'ko' && selectedPost.contentKo ? selectedPost.contentKo :
                          language === 'en' && selectedPost.contentEn ? selectedPost.contentEn :
                          selectedPost.content
                }} />
              </div>
            ) : selectedCategory ? (
              // Post List View
              <>
                <button className="back-btn" onClick={handleBackToCategories}>
                  ← {t('back')}
                </button>
                <div className="info-posts-list">
                  {loading ? (
                    <div className="empty-state">
                      <p>{t('loading')}</p>
                    </div>
                  ) : infoPosts.length === 0 ? (
                    <div className="empty-state">
                      <p>{t('noPosts')}</p>
                    </div>
                  ) : (
                    infoPosts.map((post) => (
                      <div
                        key={post.id}
                        className="info-post-card"
                        onClick={() => fetchPostDetail(post.id)}
                      >
                        <div className="post-icon">{post.icon}</div>
                        <div className="post-info">
                          <h3 className="post-card-title">
                            {language === 'ko' && post.titleKo ? post.titleKo :
                             language === 'en' && post.titleEn ? post.titleEn :
                             post.title}
                            {post.isPinned && <span className="pin-badge">📌</span>}
                          </h3>
                          {(post.summary || post.summaryEn || post.summaryKo) && (
                            <p className="post-summary">
                              {language === 'ko' && post.summaryKo ? post.summaryKo :
                               language === 'en' && post.summaryEn ? post.summaryEn :
                               post.summary}
                            </p>
                          )}
                          <div className="post-footer">
                            <span className="post-date">
                              {new Date(post.publishedAt || post.createdAt).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')}
                            </span>
                            <span className="post-views">👁 {post.viewCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              // Category List View
              <div className="info-sections-grid">
                {infoSections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <div
                      key={section.id}
                      className="info-card"
                      onClick={() => handleCategoryClick(section.id)}
                    >
                      <div className="info-card-icon">
                        <IconComponent size={24} color="white" />
                      </div>
                      <div className="info-card-content">
                        <h3 className="info-card-title">{section.title}</h3>
                        <p className="info-card-description">{section.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab Content */}
        {activeTab !== 'info' && (
          <>
            {notifications.length === 0 ? (
              <div className="empty-state">
                <Bell size={48} />
                <p>{t('noNotifications')}</p>
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                    onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}
                  >
                    {getNotificationIcon(notification.type)}

                    <div className="notification-content">
                      <h3 className="notification-title">{notification.title}</h3>
                      <p className="notification-message">{notification.message}</p>
                      <div className="notification-time">
                        <Clock size={12} />
                        {getTimeAgo(notification.createdAt)}
                      </div>
                    </div>

                    {!notification.isRead && <div className="unread-dot"></div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default NotificationsPage;
